import type { ApiKey, KeyCreator } from '../types/api'
import { formatDate } from './format'
import { lastUsedLabel, type LastUsedLabel } from './lastUsed'

/** One key as the page shows it: a secret and the secrets self-service rotation made from it. */
export interface KeyChain {
  /** The root secret's id. */
  id: string
  /** The oldest listed secret: its predecessor, if any, is gone. */
  root: ApiKey
  /** The newest secret. */
  current: ApiKey
  /** Newest first, the current secret included. */
  secrets: ApiKey[]
  /** Unexpired secrets other than the current one, newest first. */
  olderWorking: ApiKey[]
  createdBy: KeyCreator | null
  /** When the root secret was made. */
  createdAt: string
  /** When rotation made the current secret; null if rotation did not make it. */
  lastRotatedAt: string | null
  /** The newest last use of any secret. */
  lastUsedAt: string | null
}

const time = (value: string) => new Date(value).getTime()

function newestFirst(a: ApiKey, b: ApiKey): number {
  return time(b.created_at) - time(a.created_at) || a.id.localeCompare(b.id)
}

function toChain(root: ApiKey, members: ApiKey[]): KeyChain {
  const secrets = [...members].sort(newestFirst)
  const current = secrets[0]
  const uses = secrets.map((s) => s.last_used_at).filter((used): used is string => !!used)
  return {
    id: root.id,
    root,
    current,
    secrets,
    olderWorking: secrets.slice(1).filter((s) => s.status === 'active'),
    createdBy: current.created_by ?? null,
    createdAt: root.created_at,
    lastRotatedAt: current.rotated_from_key_id ? current.created_at : null,
    lastUsedAt: uses.length ? uses.reduce((a, b) => (time(b) > time(a) ? b : a)) : null,
  }
}

/** Groups secrets by rotated_from_key_id, never by name. Chains keep the API's order, at their current secret. */
export function buildKeyChains(keys: ApiKey[]): KeyChain[] {
  const byId = new Map(keys.map((key) => [key.id, key]))
  const rootOf = (key: ApiKey): ApiKey => {
    const seen = new Set<string>()
    let node = key
    while (node.rotated_from_key_id && !seen.has(node.id)) {
      seen.add(node.id)
      const parent = byId.get(node.rotated_from_key_id)
      if (!parent) break
      node = parent
    }
    return node
  }

  const groups = new Map<string, ApiKey[]>()
  for (const key of keys) {
    const rootId = rootOf(key).id
    groups.set(rootId, [...(groups.get(rootId) ?? []), key])
  }
  const position = new Map(keys.map((key, index) => [key.id, index]))
  return [...groups.entries()]
    .map(([rootId, members]) => toChain(byId.get(rootId)!, members))
    .sort((a, b) => position.get(a.current.id)! - position.get(b.current.id)!)
}

/** "Last used" for a key: its newest use, or "Never" / "Not since" judged by when the key was created. */
export function chainLastUsedLabel(
  chain: KeyChain,
  trackedSince: string | null | undefined,
  now: Date,
): LastUsedLabel | null {
  return lastUsedLabel({ last_used_at: chain.lastUsedAt, created_at: chain.createdAt }, trackedSince, now)
}

/** How the page names a secret: "ab3…". */
export function secretLabel(secret: ApiKey): string {
  return secret.hint ? `${secret.hint}…` : `secret ${secret.id.slice(0, 8)}`
}

/** A secret's state within its key. */
export function secretState(secret: ApiKey, chain: KeyChain): string {
  if (secret.id === chain.current.id) return 'Current'
  return secret.status === 'active'
    ? `Works until ${formatDate(secret.expires_at)}`
    : `Expired ${formatDate(secret.expires_at)}`
}
